'use client';

import { useEffect, useState } from 'react';
import { subscribeToSoldCount } from '@/lib/realtime/liveCompetitions';

/**
 * Tracks the live "tickets sold" count for a competition.
 *
 * - Seeds from a server-rendered initial value so the first paint
 *   is instant (no spinner, SEO-friendly).
 * - Listens on the shared comp:counts channel: the database announces a
 *   competition's tickets_sold whenever it changes. One channel per tab,
 *   however many counters and progress bars are on the page, so a purchase
 *   reaches each visitor once (it used to reach every bar separately, with
 *   every competition's changes).
 * - Re-reads every counter on the page in one query on mount (the page may
 *   be a cached render), after a reconnect, when the tab regains focus and
 *   once a minute, so a missed message can't leave a count behind.
 *
 * Returns the clamped sold count (never below 0 or above total).
 */
export function useLiveTicketsSold(competitionId, initialSold = 0, totalTickets = Infinity) {
  const [sold, setSold] = useState(initialSold || 0);

  useEffect(() => {
    if (!competitionId) return undefined;
    return subscribeToSoldCount(competitionId, setSold);
  }, [competitionId]);

  const total = typeof totalTickets === 'number' && totalTickets > 0 ? totalTickets : Infinity;
  return Math.min(Math.max(0, sold || 0), total);
}
